import { useEffect, useState } from "react";

import { Empty, Modal as AntModal } from "antd";
import { CloseOutlined, SearchOutlined } from "@ant-design/icons";

import { Input } from "../../../../shared";
import RebetLoader from "../../../../CommonComponents/RebetLoader/RebetLoader";
import {
  type AdminCollection,
  useAvailableCollections,
} from "../../../../../queries/casino-configuration/collections/useAdminCollections";
import { AppStrings } from "../../../../../utils/constants/AppStrings";
import { useCurationDisplayName } from "../../../../../hooks/useCurationDisplayName";
import CurationEntityName from "../../CurationEntityName";
import { formatString } from "../../../../../utils/constants/stringUtils";

import "../stylesCollectionsConfiguration.css";

const PARENT_PICKER_SEARCH_DEBOUNCE_MS = 300;

export type EditParentPickerModalProps = {
  open: boolean;
  // Forwarded as `?exclude_code=...` so the collection can't be its own parent.
  currentCode: string;
  // "games" | "providers" — picker filters to same-kind candidates.
  kind: string;
  // null while no parent picked / Top-level highlighted.
  selectedCode: string | null;
  // null = "Top-level" (detach / make top-level).
  onSelect: (code: string | null) => void;
  onClose: () => void;
  // Hide the Top-level row. Caller passes true when the picker is in
  // "change" mode (a parent is already set) — clearing should go through
  // the explicit × button on the parent pill, not a silent re-pick that
  // happens to mean "detach".
  hideTopLevel?: boolean;
};

// Edit-flow parent picker. Mirrors NewCollectionModal's ParentPickerModal
// but uses `useAvailableCollections` for `?exclude_code=...` (the create
// flow has no code yet). Reuses `nc-parent-*` CSS for visual parity.
export default function EditParentPickerModal({
  open,
  currentCode,
  kind,
  selectedCode,
  onSelect,
  onClose,
  hideTopLevel = false,
}: EditParentPickerModalProps) {
  const { getName } = useCurationDisplayName();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearch(search),
      PARENT_PICKER_SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [search]);

  // Reset the search box every time the picker opens so reopening doesn't
  // surface a stale filter from a previous session.
  useEffect(() => {
    if (open) {
      setSearch("");
      setDebouncedSearch("");
    }
  }, [open]);

  const { data, isLoading } = useAvailableCollections(
    open ? currentCode : null,
    kind,
    debouncedSearch || undefined,
  );

  const items: AdminCollection[] = data?.items ?? [];

  return (
    <AntModal
      open={open}
      onCancel={onClose}
      footer={null}
      width={520}
      className="cd-add-modal nc-parent-modal"
      destroyOnHidden
      closeIcon={<CloseOutlined />}
      title={
        <div>
          <div className="cd-add-title">
            {AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_PARENT_TITLE}
          </div>
          <div className="cd-add-selected">
            {formatString(
              AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_PARENT_SUBTITLE,
              { kind },
            )}
          </div>
        </div>
      }
    >
      <Input
        className="cd-add-search"
        prefix={<SearchOutlined />}
        placeholder={AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_PARENT_SEARCH}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        allowClear
        data-testid="edit-parent-picker-search"
      />

      <div className="cd-add-list nc-parent-list">
        {hideTopLevel ? null : (
          <button
            type="button"
            className="nc-parent-row nc-parent-row--top"
            onClick={() => onSelect(null)}
            data-testid="edit-parent-picker-top-level"
          >
            <div className="nc-parent-row-text">
              <div className="nc-parent-row-name">
                {AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_PARENT_TOP_LEVEL}
              </div>
              <div className="nc-parent-row-hint">
                {
                  AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_PARENT_TOP_LEVEL_HINT
                }
              </div>
            </div>
          </button>
        )}

        {isLoading ? (
          <div className="cd-games-loader">
            <RebetLoader height="32px" width="32px" />
          </div>
        ) : items.length === 0 ? (
          <Empty
            description={
              AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_PARENT_NO_RESULTS
            }
          />
        ) : (
          items.map((item) => {
            const isSelected = selectedCode === item.code;
            return (
              <button
                type="button"
                key={item.code}
                className={`nc-parent-row ${
                  isSelected ? "nc-parent-row--selected" : ""
                }`}
                onClick={() => onSelect(item.code)}
                data-testid={`edit-parent-picker-row-${item.code}`}
              >
                <div className="nc-parent-row-text">
                  <div className="nc-parent-row-name">
                    <CurationEntityName
                      entity={item}
                      displayName={getName(item)}
                    />
                  </div>
                  <div className="nc-parent-row-hint">{item.code}</div>
                </div>
                {isSelected && (
                  <span className="nc-parent-row-selected-label">
                    {AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_PARENT_SELECTED}
                  </span>
                )}
              </button>
            );
          })
        )}
      </div>

      <div className="nc-parent-footer">
        <button
          type="button"
          className="cd-add-cancel-btn nc-parent-close-btn"
          onClick={onClose}
        >
          {AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_PARENT_CLOSE}
        </button>
      </div>
    </AntModal>
  );
}
